<template>
  <section class="form-list">
    <div v-show="isLoading || isSaving || isLoadingError" class="loading-box">
      <div class="loading-main" @click="load">
        <svg v-show="isLoading || isSaving" class="loading-circular" viewBox="25 25 50 50">
          <circle class="loading-path" cx="50" cy="50" r="20" fill="none"/>
        </svg>
        <div v-show="isLoadingError" class="loading-refresh">
          <i class="el-icon-refresh"/>
        </div>
        <div v-show="isLoadingError" class="loading-text">
          数据加载失败，点击重新尝试
        </div>
      </div>
    </div>

    <el-form
      ref="form"
      :model="report"
      label-position="left"
      label-width="160px"
      class="form-box"
    >
      <el-form-item label="所属月份">
        <el-date-picker
          v-model="report.report_date"
          :picker-options="pickerOptionsOfAfterBeforeToday"
          type="month"
          value-format="timestamp"
          placeholder="请选择所属月份"
          class="expenditure-input"
        />
      </el-form-item>

      <div class="table-box">
        <h3>门店收入提成</h3>
        <el-table
          :data="storeIncomes"
          class="income-table store-income-table"
          border
          fit
          highlight-current-row
          :show-summary="true"
          :summary-method="getStoreSummaries"
        >
          <el-table-column label="门店" align="center" width="154">
            <template v-slot="{ row }">
              <v-select-store v-model="row.store_id"/>
            </template>
          </el-table-column>
          <el-table-column label="总收入" align="center" prop="all_income" width="186">
            <template v-slot="{ row }">
              <v-input-money
                v-model="row.all_income"
                :is-show-suffix="true"
                @input="computeCommission(row)"
              />
            </template>
          </el-table-column>
          <el-table-column label="总利润" align="center" prop="profit" width="186">
            <template v-slot="{ row }">
              <v-input-money
                v-model="row.profit"
                type="int"
                :is-show-suffix="true"
                @input="computeCommission(row)"
              />
            </template>
          </el-table-column>
          <el-table-column label="提成百分比" align="center" width="150">
            <template v-slot="{ row }">
              <v-input-money
                v-model="row.revenue_sharing"
                :is-show-suffix="true"
                suffix-name="%"
                @input="computeCommission(row)"
              />
            </template>
          </el-table-column>
          <el-table-column label="提成" align="center" prop="commission" width="186">
            <template v-slot="{ row }">
              <v-input-money
                v-model="row.commission"
                :is-show-suffix="true"
                @input="computeStoreIncome"
              />
            </template>
          </el-table-column>
          <el-table-column label="提成占利润比" align="center" width="130">
            <template v-slot="{ row }">
              {{ row.profit ? `${decimalPlaces(dividedBy(row.commission, row.profit, .01))} %` : '' }}
            </template>
          </el-table-column>
          <el-table-column
            label="操作"
            align="center"
            class-name="small-padding fixed-width"
          >
            <template v-slot="{ $index, row }">
              <el-popover placement="top" v-model="row.showTip">
                <p>确认要删除吗？</p>
                <div style="text-align: right; margin: 0">
                  <el-button size="mini" type="text" @click="row.showTip = false">取消</el-button>
                  <el-button
                    type="primary"
                    size="mini"
                    @click="handleDeleteStoreIncomes($index)"
                  >
                    确认
                  </el-button>
                </div>
                <el-button class="edit-button" slot="reference" size="mini" type="danger">删除
                </el-button>
              </el-popover>
            </template>
          </el-table-column>
        </el-table>
        <el-button class="add-button" type="primary" @click="addStoreIncomes">新增一项</el-button>
      </div>

      <div class="table-box">
        <h3>员工工资</h3>
        <el-table
          :data="staffSalaries"
          class="income-table"
          border
          fit
          highlight-current-row
          :show-summary="true"
          :summary-method="getStaffSummaries"
        >
          <el-table-column label="员工" align="center" width="160">
            <template v-slot="{ row }">
              <v-select-administrator
                :store-id="userStoreId"
                v-model="row.administrator_id"
                placeholder="请选择员工"
              />
            </template>
          </el-table-column>
          <el-table-column label="底薪" align="center" prop="base_pay" width="186">
            <template v-slot="{ row }">
              <v-input-money :is-show-suffix="true" v-model="row.base_pay"/>
            </template>
          </el-table-column>
          <el-table-column label="提成" align="center" prop="percentage_pay" width="186">
            <template v-slot="{ row }">
              <v-input-money :is-show-suffix="true" v-model="row.percentage_pay"/>
            </template>
          </el-table-column>
          <el-table-column label="公司社保" align="center" prop="social_insurance" width="186">
            <template v-slot="{ row }">
              <v-input-money :is-show-suffix="true" v-model="row.social_insurance"/>
            </template>
          </el-table-column>
          <el-table-column label="个人社保" align="center" prop="personal_insurance" width="186">
            <template v-slot="{ row }">
              <v-input-money :is-show-suffix="true" v-model="row.personal_insurance"/>
            </template>
          </el-table-column>
          <el-table-column label="奖金" align="center" prop="bonus" width="186">
            <template v-slot="{ row }">
              <v-input-money :is-show-suffix="true" v-model="row.bonus"/>
            </template>
          </el-table-column>
          <el-table-column label="实发" align="center" prop="actual_pay">
            <template v-slot="{ row }">
              {{ computeStaffActualPayment(row) }}
            </template>
          </el-table-column>
          <el-table-column label="总计" align="center" prop="all_pay">
            <template v-slot="{ row }">
              <span class="success-color">{{ computeStaffAllPayment(row) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            label="操作"
            align="center"
            class-name="small-padding fixed-width"
          >
            <template v-slot="{ $index, row }">
              <el-popover placement="top" v-model="row.showTip">
                <p>确认要删除吗？</p>
                <div style="text-align: right; margin: 0">
                  <el-button size="mini" type="text" @click="row.showTip = false">取消</el-button>
                  <el-button
                    type="primary"
                    size="mini"
                    @click="handleDelete($index, staffSalaries)"
                  >
                    确认
                  </el-button>
                </div>
                <el-button class="edit-button" slot="reference" size="mini" type="danger">删除
                </el-button>
              </el-popover>
            </template>
          </el-table-column>
        </el-table>
        <el-button class="add-button" type="primary" @click="addStaffSalaries">新增一项</el-button>
      </div>

      <div class="table-box">
        <h3>收入统计</h3>
        <el-form-item label="提成店数量">
          <v-input-number
            class="expenditure-input"
            v-model="report.store_number"
            placeholder="请输入提成店数量"
            :is-show-suffix="true"
            suffix-name="个"
          />
        </el-form-item>
        <el-form-item label="门店提成收入">
          <v-input-money
            class="expenditure-input"
            v-model="report.store_income"
            placeholder="请输入门店提成收入"
            :is-show-suffix="true"
          />
        </el-form-item>
        <el-form-item label="大师课数量">
          <v-input-number
            class="expenditure-input"
            v-model="report.master_number"
            placeholder="请输入大师课数量"
            :is-show-suffix="true"
            suffix-name="节"
          />
        </el-form-item>
        <el-form-item label="大师课收入">
          <v-input-money
            class="expenditure-input"
            v-model="report.master_income"
            placeholder="请输入大师课收入"
            :is-show-suffix="true"
          />
        </el-form-item>
        <el-form-item label="有赞收入">
          <v-input-money
            class="expenditure-input"
            v-model="report.you_zan_income"
            placeholder="请输入有赞收入"
            :is-show-suffix="true"
          />
        </el-form-item>
        <el-form-item label="其他收入">
          <v-input-money
            class="expenditure-input"
            v-model="report.other_income"
            placeholder="请输入其他收入"
            :is-show-suffix="true"
          />
        </el-form-item>
        <el-form-item label="其他收入备注">
          <el-input
            type="textarea"
            :rows="4"
            v-model="report.other_income_remark"
            class="expenditure-input"
            maxlength="250"
            show-word-limit
          />
        </el-form-item>
      </div>

      <div class="table-box">
        <h3>支出统计</h3>
        <el-form-item label="房租支出">
          <v-input-money
            class="expenditure-input"
            v-model="report.house_pay"
            placeholder="请输入房租支出"
            :is-show-suffix="true"
          />
        </el-form-item>
        <el-form-item label="员工工资支出">
          <v-input-money
            class="expenditure-input"
            v-model="report.staff_pay"
            placeholder="请输入员工工资支出"
            :is-show-suffix="true"
          />
        </el-form-item>
        <el-form-item label="保洁工资支出">
          <v-input-money
            class="expenditure-input"
            v-model="report.cleaner_pay"
            placeholder="请输入保洁工资支出"
            :is-show-suffix="true"
          />
        </el-form-item>
        <el-form-item label="水电费支出">
          <v-input-money
            class="expenditure-input"
            v-model="report.water_pay"
            placeholder="请输入水电费支出"
            :is-show-suffix="true"
          />
        </el-form-item>
        <el-form-item label="有赞核销支出">
          <v-input-money
            class="expenditure-input"
            v-model="report.you_zan_pay"
            placeholder="请输入有赞核销支出"
            :is-show-suffix="true"
          />
        </el-form-item>
        <el-form-item label="其他支出">
          <v-input-money
            class="expenditure-input"
            v-model="report.other_pay"
            placeholder="请输入其他支出"
            :is-show-suffix="true"
          />
        </el-form-item>
        <el-form-item label="其他支出备注">
          <el-input
            type="textarea"
            :rows="4"
            v-model="report.other_pay_remark"
            class="expenditure-input"
            maxlength="250"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="余额">
          <v-input-money
            class="expenditure-input"
            v-model="report.balance"
            placeholder="请输入余额"
            :is-show-suffix="true"
          />
        </el-form-item>
        <el-form-item label="加盟店押金">
          <v-input-money
            class="expenditure-input"
            v-model="report.store_deposit"
            placeholder="请输入加盟店押金"
            :is-show-suffix="true"
          />
        </el-form-item>
      </div>

      <div class="table-box">
        <div class="flex-align-center">
          <h3 class="profit-title">总收入</h3>
          <h2 class="danger-color">{{ report.all_income }}</h2>
        </div>
        <div class="flex-align-center">
          <h3 class="profit-title">总支出</h3>
          <h2 class="success-color">{{ computeExpenditure(report) }}</h2>
        </div>
        <div class="flex-align-center">
          <h3 class="profit-title">总利润</h3>
          <h2 :class="`${report.profit >= 0 ? 'danger' : 'success'}-color`">{{ computeProfit(report) }}</h2>
        </div>
      </div>

      <el-button v-if="report.is_draft" type="success" @click="handleSubmit(0)" :loading="isSaving">立即发布</el-button>
      <el-button type="primary" @click="handleSubmit(report.is_draft)" :loading="isSaving">保存修改</el-button>
      <el-button @click="RouteConfig.officeMonthlyReportList.back">返回</el-button>
    </el-form>
  </section>
</template>

<script>
import RouteConfig from '@/router/config';
import { mapGetters } from 'vuex';
import {
  show,
  getStaffSalariesByReportId,
  getStoreIncomesByReportId,
  update
} from '@/api/office-monthly-report';
import { getSummaries } from "@/views/office-monthly-report/getSummary";
import { pickerOptionsOfAfterBeforeToday } from '@/utils/formats';
import {
  toCent,
  toYuan,
  toMillisecond,
  toSecond,
  plus,
  minus,
  multipliedBy,
  dividedBy, decimalPlaces
} from "@/filters";

export default {
  name: RouteConfig.officeMonthlyReportEdit.name,
  data: () => ({
    RouteConfig,
    pickerOptionsOfAfterBeforeToday,
    isLoading: false,
    isLoadingError: false,
    isSaving: false,
    staffSalaries: [],
    storeIncomes: [],
    report: {
      report_date: '',
      store_number: 0,
      store_income: 0,
      master_number: 0,
      master_income: 0,
      you_zan_income: 0,
      other_income: 0,
      other_income_remark: '',
      all_income: 0,
      house_pay: 0,
      staff_pay: 0,
      cleaner_pay: 0,
      water_pay: 0,
      you_zan_pay: 0,
      other_pay: 0,
      other_pay_remark: '',
      expenditure: 0,
      balance: 0,
      profit: 0,
      store_deposit: 0,
      is_draft: 0,
    },
  }),
  computed: {
    ...mapGetters([
      'isAdministrator',
      'userStoreId',
    ]),
    id() {
      return +this.$route.params.id || 0;
    },
  },
  watch: {
    'report.store_income'() {
      this.computeAllIncome();
    },
    'report.master_income'() {
      this.computeAllIncome();
    },
    'report.other_income'() {
      this.computeAllIncome();
    },
    'report.you_zan_income'() {
      this.computeAllIncome();
    },
  },
  beforeMount() {
    this.load();
  },
  methods: {
    dividedBy,
    decimalPlaces,
    addStaffSalaries() {
      this.staffSalaries.push({
        administrator_id: '',
        base_pay: 0,
        bonus: 0,
        percentage: 0,
        personal_insurance: 0,
        social_insurance: 0,
      });
    },
    addStoreIncomes() {
      this.storeIncomes.push({
        store_id: 0,
        all_income: 0,
        profit: 0,
        revenue_sharing: 0,
        commission: 0,
      });
    },
    handleDelete(index, rows) {
      rows.splice(index, 1);
    },
    handleDeleteStoreIncomes(index) {
      this.storeIncomes.splice(index, 1);
      this.computeStoreIncome();
    },
    computeStaffPay() {
      let staffPay = 0;
      this.staffSalaries.forEach(v => staffPay = plus(staffPay, v.all_pay));
      this.report.staff_pay = staffPay;
    },
    computeStaffActualPayment(row) {
      const { base_pay, percentage_pay, bonus, personal_insurance } = row;
      row.actual_pay = minus(plus(base_pay, percentage_pay, bonus), personal_insurance);
      this.computeStaffPay();
      return row.actual_pay;
    },
    computeStaffAllPayment(row) {
      // 总计 = 底薪 + 提成 + 奖金 + 公司社保（不含个人社保）
      const { base_pay, percentage_pay, bonus, social_insurance } = row;
      row.all_pay = plus(base_pay, percentage_pay, bonus, social_insurance);
      this.computeStaffPay();
      return row.all_pay;
    },
    computeStoreIncome() {
      let storeIncome = 0;
      this.storeIncomes.forEach(v => storeIncome = plus(storeIncome, v.commission));
      this.report.store_income = storeIncome;
    },
    computeCommission(row) {
      let { all_income, profit, revenue_sharing } = row;

      let commission = 0;
      if (profit > 0) {// 盈利时，总部抽成 = 总收入 * 抽成比例
        let maxCommission = multipliedBy(profit, 0.4);

        revenue_sharing = dividedBy(revenue_sharing, 100);
        commission = multipliedBy(all_income, revenue_sharing);
        commission = Math.min(commission, maxCommission);
      } else {
        commission = multipliedBy(all_income, 0.06);
      }

      row.commission = decimalPlaces(commission);
      this.computeStoreIncome();
      return row.commission;
    },

    computeExpenditure({
      house_pay,
      staff_pay,
      cleaner_pay,
      water_pay,
      you_zan_pay,
      other_pay,
    }) {
      return this.report.expenditure = plus(house_pay, staff_pay, cleaner_pay, water_pay, you_zan_pay, other_pay);
    },
    computeProfit({ all_income, expenditure }) {
      return this.report.profit = minus(all_income, expenditure);
    },

    computeAllIncome() {
      const { store_income, master_income, you_zan_income, other_income } = this.report;
      this.report.all_income = plus(store_income, master_income, you_zan_income, other_income);
    },

    getStaffSummaries(param) {
      return getSummaries({
        param,
        report: this.report,
        allPayField: 'staff_pay',
      });
    },
    getStoreSummaries(param) {
      return getSummaries({
        param,
        report: this.report,
        allPayField: 'store_income',
      });
    },
    async show() {
      const { isError, data } = await show(this.id);
      if (isError) return this.isLoadingError = true;

      Object.assign(this.report, {
        report_date: toMillisecond(data.report_date),
        store_number: data.store_number || 0,
        store_income: toYuan(data.store_income),
        master_number: data.master_number || 0,
        master_income: toYuan(data.master_income),
        you_zan_income: toYuan(data.you_zan_income),
        other_income: toYuan(data.other_income),
        other_income_remark: data.other_income_remark || '',
        all_income: toYuan(data.all_income),
        house_pay: toYuan(data.house_pay),
        staff_pay: toYuan(data.staff_pay),
        cleaner_pay: toYuan(data.cleaner_pay),
        water_pay: toYuan(data.water_pay),
        you_zan_pay: toYuan(data.you_zan_pay),
        other_pay: toYuan(data.other_pay),
        other_pay_remark: data.other_pay_remark || '',
        expenditure: toYuan(data.expenditure),
        balance: toYuan(data.balance),
        profit: toYuan(data.profit),
        store_deposit: toYuan(data.store_deposit),
        is_draft: data.is_draft ? 1 : 0,
      });
    },
    async getStaffSalaries() {
      const { isError, data } = await getStaffSalariesByReportId(this.id);

      if (isError) {
        this.isLoadingError = true;
        return;
      }

      data.forEach(v => {
        v.base_pay = toYuan(v.base_pay);
        v.percentage_pay = toYuan(v.percentage_pay);
        v.social_insurance = toYuan(v.social_insurance);
        v.personal_insurance = toYuan(v.personal_insurance);
        v.bonus = toYuan(v.bonus);
      });

      this.staffSalaries = data;
    },
    async getStoreIncomes() {
      const { isError, data } = await getStoreIncomesByReportId(this.id);

      if (isError) {
        this.isLoadingError = true;
        return;
      }

      data.forEach(v => {
        v.all_income = toYuan(v.all_income);
        v.profit = toYuan(v.profit);
        v.revenue_sharing = toYuan(v.revenue_sharing);
        v.commission = toYuan(v.commission);
      });

      this.storeIncomes = data;
    },
    async load() {
      if (this.isLoading) return;

      this.isLoading = true;
      this.isLoadingError = false;

      await Promise.all([
        this.show(),
        this.getStoreIncomes(),
        this.getStaffSalaries(),
      ]);

      this.isLoading = false;
    },
    handleSubmit(is_draft) {
      if (this.isSaving) return;

      this.$refs.form.validate(async (isSuccess) => {
        if (!isSuccess) return;

        const postData = { ...this.report };
        postData.is_draft = is_draft;
        postData.report_date = toSecond(postData.report_date);

        // 收入
        postData.store_income = toCent(postData.store_income);
        postData.master_income = toCent(postData.master_income);
        postData.you_zan_income = toCent(postData.you_zan_income);
        postData.other_income = toCent(postData.other_income);
        postData.all_income = toCent(postData.all_income);

        // 支出
        postData.house_pay = toCent(postData.house_pay);
        postData.staff_pay = toCent(postData.staff_pay);
        postData.cleaner_pay = toCent(postData.cleaner_pay);
        postData.water_pay = toCent(postData.water_pay);
        postData.you_zan_pay = toCent(postData.you_zan_pay);
        postData.other_pay = toCent(postData.other_pay);
        postData.expenditure = toCent(postData.expenditure);
        postData.balance = toCent(postData.balance);
        postData.profit = toCent(postData.profit);
        postData.store_deposit = toCent(postData.store_deposit);

        const store_incomes = JSON.parse(JSON.stringify(this.storeIncomes));
        store_incomes.forEach(v => {
          v.all_income = toCent(v.all_income);
          v.profit = toCent(v.profit);
          v.revenue_sharing = toCent(v.revenue_sharing);
          v.commission = toCent(v.commission);
        });

        const staff_salaries = JSON.parse(JSON.stringify(this.staffSalaries));
        staff_salaries.forEach(v => {
          v.base_pay = toCent(v.base_pay);
          v.percentage_pay = toCent(v.percentage_pay);
          v.social_insurance = toCent(v.social_insurance);
          v.personal_insurance = toCent(v.personal_insurance);
          v.bonus = toCent(v.bonus);
          v.actual_pay = toCent(v.actual_pay);
          v.all_pay = toCent(v.all_pay);
        });

        Object.assign(postData, {
          store_incomes,
          staff_salaries,
        });

        this.isSaving = true;
        const { isError } = await update(this.id, postData);
        this.isSaving = false;
        if (isError) return;

        RouteConfig.officeMonthlyReportList.back();
      });
    },
  },
};
</script>

<style src="../monthly-report/add.css" scoped/>
